import React, { useState, useEffect } from 'react';
import { getAccounts, getInsights, getAccountInfo, clearCache } from '../services/api';
import { formatCurrency, formatNumber, formatPercent, formatFrequency, formatCostPerConversation } from '../utils/formatters';
import KPICard from './KPICard';
import PerformanceChart from './PerformanceChart';
import CostPerConversationChart from './CostPerConversationChart';
import FunnelChart from './FunnelChart';
import CampaignTable from './CampaignTable';
import RadarIcon from './RadarIcon';
import '../styles/Dashboard.css';

const Dashboard = ({ user, onLogout }) => {
  const isAdmin = user && user.role === 'admin';
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [data, setData] = useState(null);
  const [accountInfo, setAccountInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('last_30d');
  const [refreshing, setRefreshing] = useState(false);

  // Carregar lista de contas disponíveis
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const response = await getAccounts();
        setAccounts(response.accounts);

        // Selecionar primeira conta por padrão
        if (response.accounts.length > 0) {
          setSelectedAccount(response.accounts[0].id);
        }
      } catch (err) {
        setError('Erro ao carregar contas disponíveis.');
        console.error(err);
      }
    };

    loadAccounts();
  }, []);

  // Buscar dados quando conta ou período mudar
  const fetchData = async () => {
    if (!selectedAccount) return;

    try {
      setLoading(true);
      setError(null);

      const [insightsData, accountData] = await Promise.all([
        getInsights(selectedAccount, period),
        getAccountInfo(selectedAccount)
      ]);

      setData(insightsData);
      setAccountInfo(accountData);
    } catch (err) {
      setError('Erro ao carregar dados. Verifique suas credenciais e conexão.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount, period]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await clearCache();
      await fetchData();
    } catch (err) {
      console.error('Erro ao atualizar:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleAccountChange = (e) => {
    setSelectedAccount(e.target.value);
  };

  if (loading && !data) {
    return <div className="loading">Carregando dados...</div>;
  }

  if (error && !data) {
    return <div className="error">{error}</div>;
  }

  if (!data || !data.summary) {
    return <div className="error">Nenhum dado disponível</div>;
  }

  const { summary, daily, campaigns } = data;
  // Moeda real da conta selecionada (backend retorna em accountInfo.currency).
  const currency = (accountInfo && accountInfo.currency) || 'BRL';

  return (
    <div className="dashboard">
      <div className="header">
        <h1><RadarIcon size={26} /> Radar de Campanhas</h1>
        <div className="header-info">
          <div className="account-info">
            {accountInfo && (
              <>
                <strong>{accountInfo.name}</strong>
                <span className="muted"> • {accountInfo.currency}</span>
              </>
            )}
            {user && (
              <span className="muted" style={{ display: 'block', fontSize: '12px', marginTop: '4px' }}>
                {user.name || user.email}{isAdmin ? ' • Admin' : ''}
              </span>
            )}
          </div>
          <div className="controls">
            {/* Admin escolhe entre as contas; manager tem só a sua (seletor oculto). */}
            {isAdmin && accounts.length > 1 && (
              <select
                className="select"
                value={selectedAccount || ''}
                onChange={handleAccountChange}
                style={{ marginRight: '10px' }}
              >
                {accounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            )}
            <select
              className="select"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="today">Hoje</option>
              <option value="yesterday">Ontem</option>
              <option value="last_7d">Últimos 7 dias</option>
              <option value="last_14d">Últimos 14 dias</option>
              <option value="last_30d">Últimos 30 dias</option>
              <option value="this_month">Este mês</option>
              <option value="last_month">Mês passado</option>
              <option value="lifetime">Máximo (desde sempre)</option>
            </select>
            <button
              className="refresh-btn"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Atualizando...' : '🔄 Atualizar'}
            </button>
            <button
              className="refresh-btn"
              onClick={onLogout}
              style={{ marginLeft: '10px' }}
            >
              Sair
            </button>
          </div>
        </div>
      </div>

      {/* Métricas protagonistas do painel CTWA */}
      <div className="kpi-grid">
        <KPICard
          label="Gasto"
          value={formatCurrency(summary.spend, currency)}
          sublabel="Investimento total"
        />
        <KPICard
          label="Conversas iniciadas"
          value={formatNumber(summary.conversations)}
          sublabel="Mensagens iniciadas via WhatsApp"
          accent="conversas"
        />
        <KPICard
          label="Custo por conversa"
          value={formatCostPerConversation(summary.costPerConversation, currency)}
          sublabel="Custo por conversa (Meta)"
          accent="custo"
        />
        <KPICard
          label="CPM"
          value={formatCurrency(summary.cpm, currency)}
          sublabel="Custo por mil impressões"
        />
        <KPICard
          label="Frequência"
          value={formatFrequency(summary.frequency)}
          sublabel="Impressões por pessoa"
        />
      </div>

      {/* Métricas secundárias (time de tráfego) */}
      <div className="kpi-grid kpi-grid--secondary">
        <KPICard small label="Impressões" value={formatNumber(summary.impressions)} />
        <KPICard small label="Cliques" value={formatNumber(summary.clicks)} />
        <KPICard small label="CTR" value={formatPercent(summary.ctr)} />
        <KPICard small label="Alcance" value={formatNumber(summary.reach)} />
        <KPICard small label="CPC" value={formatCurrency(summary.cpc, currency)} />
      </div>

      {daily && daily.length > 0 && (
        <>
          {/* Gráfico principal em largura total */}
          <PerformanceChart data={daily} currency={currency} />
          {/* Abaixo: custo por conversa por dia + funil lado a lado */}
          <div className="charts-grid">
            <CostPerConversationChart data={daily} currency={currency} />
            <FunnelChart data={daily} />
          </div>
        </>
      )}

      {campaigns && campaigns.length > 0 && (
        <CampaignTable campaigns={campaigns} currency={currency} />
      )}

      <div className="footer-note">
        Dados atualizados automaticamente a cada 5 minutos
        {data.cached && ' • Dados em cache'}
      </div>
    </div>
  );
};

export default Dashboard;
